/**
 * SA-1329 (spec §4): the pure rules a selection of waiting or failed triage
 * messages is judged by: what each one is, what Approve, Dismiss and Change
 * kind can do with the lot, what the confirmation says and which kinds suit
 * every message. No store, no request; the run is built on these.
 */
import type {
  InboxTriage, QueueItem, ThreadRecord, TriageKindOption, TriageRow,
} from '@/types/inbox'
import { blockState, kindOption, needsChoice } from './triageBlocks'
import {
  approveReason, CHANGE_KIND_HELD, confirmHeading, confirmLine, DISMISS_HELD, FAILED_PART, kindsSummary, NEEDING_KIND,
  nounWould, rerunReason,
} from './triageText'

export type CandidateState = 'approvable' | 'needs_record' | 'needs_outcome' | 'needs_kind' | 'failed' | 'refused'

export interface BulkCandidate {
  key: string
  conversationId: string
  ledgerId: string | null
  mailboxId: string | null
  /** The block's heading, for the run's lines. */
  label: string
  state: CandidateState
  kindId: string | null
  kindLabel: string | null
  /** What approving would do, the noun already in it. */
  would: string | null
  outcome: string | null
  refusal: string | null
  canDecide: boolean
  canRerun: boolean
  dismissRefusal: string | null
  recordWords: string | null
}

interface RowContext {
  conversationId: string
  canTriage: boolean
  noun: string
  openRecords: readonly ThreadRecord[]
  label: string
}

/** The candidate a panel row makes, or null when the viewer can do nothing with it. */
export function candidateFromRow(row: TriageRow, triage: InboxTriage, ctx: RowContext): BulkCandidate | null {
  const block = blockState(row)
  const may = ctx.canTriage && row.canDecide
  if (block === 'decided') return null
  if (block === 'waiting' && !may) return null
  if (block === 'failed' && !may && !row.canRerun) return null

  const kinds = triage.kinds
  const option = kindOption(kinds, row.proposedKind)
  const internal = kindOption(kinds, 'internal')
  const base = {
    key: row.ledgerId,
    conversationId: ctx.conversationId,
    ledgerId: row.ledgerId,
    mailboxId: triage.mailboxId,
    label: ctx.label,
    kindId: row.proposedKind ?? row.kind,
    kindLabel: null as string | null,
    would: null as string | null,
    outcome: null as string | null,
    refusal: null as string | null,
    canDecide: may,
    canRerun: row.canRerun,
    // Dismiss is the `internal` kind: missing, or refused without a sentence, it is still held back in words.
    dismissRefusal: !internal ? DISMISS_HELD : internal.canDecide ? null : internal.refusal ?? DISMISS_HELD,
    recordWords: null as string | null,
  }
  if (block === 'failed') return { ...base, state: 'failed' }
  if (row.kind === 'unknown' || !row.proposedKind || !option) return { ...base, state: 'needs_kind' }

  const would = row.would ?? option.would
  const filled = {
    ...base,
    kindLabel: row.proposedLabel ?? option.label,
    would: would ? nounWould(would, ctx.noun) : null,
    outcome: option.outcomes?.length ? row.outcome ?? null : null,
    recordWords: option.takesTarget
      ? ctx.openRecords.length === 1 ? `${ctx.noun} ${ctx.openRecords[0].number}` : `a ${ctx.noun} on this thread`
      : null,
  }
  if (!option.canDecide) return { ...filled, state: 'refused', refusal: option.refusal }
  const need = needsChoice(row, kinds)
  if (need === 'record') return { ...filled, state: 'needs_record' }
  if (need === 'outcome') return { ...filled, state: 'needs_outcome' }
  return { ...filled, state: 'approvable' }
}

/** A queue item's candidate before the press; the press judges it again from the thread. */
export function candidateFromItem(item: QueueItem): BulkCandidate {
  const state: CandidateState = item.flag === 'handler_failed' ? 'failed'
    : item.flag === 'needs_triage' ? 'needs_kind' : 'approvable'
  const first = item.kinds[0]
  return {
    key: item.id,
    conversationId: item.conversationId,
    ledgerId: item.ledgerId,
    mailboxId: item.mailboxId,
    label: item.title ?? item.senderName ?? '',
    state,
    kindId: first?.kind ?? item.kind,
    kindLabel: first?.kindLabel ?? item.kindLabel,
    would: null,
    outcome: null,
    refusal: null,
    canDecide: true,
    canRerun: state === 'failed',
    dismissRefusal: null,
    recordWords: null,
  }
}

export interface BulkVerdict {
  approve: { mode: 'approve' | 'rerun'; enabled: boolean; reason: string | null }
  dismiss: { enabled: boolean; reason: string | null }
  changeKind: { enabled: boolean; reason: string | null }
  summary: string
}

function count(cands: readonly BulkCandidate[], state: CandidateState): number {
  return cands.filter((c) => c.state === state).length
}

function summaryOf(cands: readonly BulkCandidate[]): string {
  const kinds = new Map<string, number>()
  let needing = 0
  let failed = 0
  for (const c of cands) {
    if (c.state === 'failed') failed += 1
    else if (c.state === 'needs_kind' || !c.kindLabel) needing += 1
    else kinds.set(c.kindLabel, (kinds.get(c.kindLabel) ?? 0) + 1)
  }
  const parts: [string, number][] = [...kinds]
  if (needing) parts.push([NEEDING_KIND, needing])
  if (failed) parts.push([FAILED_PART, failed])
  return kindsSummary(parts)
}

/** What Approve, Dismiss and Change kind can do with this selection, and why not. */
export function bulkVerdict(cands: readonly BulkCandidate[], noun: string): BulkVerdict {
  if (!cands.length) {
    return {
      approve: { mode: 'approve', enabled: false, reason: null },
      dismiss: { enabled: false, reason: null }, changeKind: { enabled: false, reason: null }, summary: '',
    }
  }
  const allFailed = cands.every((c) => c.state === 'failed')
  let approve: BulkVerdict['approve']
  if (allFailed) {
    const refusedRerun = cands.filter((c) => !c.canRerun).length
    approve = { mode: 'rerun', enabled: refusedRerun === 0, reason: refusedRerun ? rerunReason(refusedRerun) : null }
  } else {
    const record = count(cands, 'needs_record')
    const outcome = count(cands, 'needs_outcome')
    const kind = count(cands, 'needs_kind')
    const failed = count(cands, 'failed')
    const refused = cands.find((c) => c.state === 'refused')
    const reason = record ? approveReason.record(noun, record)
      : outcome ? approveReason.outcome(outcome)
        : kind ? approveReason.kind(kind)
          : failed ? approveReason.rerun(failed)
            : refused ? refused.refusal ?? approveReason.refused : null
    approve = { mode: 'approve', enabled: reason === null, reason }
  }
  const everyDecides = cands.every((c) => c.canDecide)
  const held = cands.find((c) => c.dismissRefusal)?.dismissRefusal ?? null
  const dismissOk = everyDecides && !held
  return {
    approve,
    dismiss: { enabled: dismissOk, reason: dismissOk ? null : held ?? DISMISS_HELD },
    changeKind: { enabled: everyDecides, reason: everyDecides ? null : CHANGE_KIND_HELD },
    summary: summaryOf(cands),
  }
}

/** The confirmation: one line per kind, the records it lands on beside it. */
export function confirmText(cands: readonly BulkCandidate[]): { heading: string; lines: string[] } {
  const groups = new Map<string, { count: number; records: string[]; would: string | null }>()
  for (const c of cands) {
    const label = c.kindLabel ?? c.label
    const group = groups.get(label) ?? { count: 0, records: [], would: c.would }
    group.count += 1
    if (c.recordWords && !group.records.includes(c.recordWords)) group.records.push(c.recordWords)
    groups.set(label, group)
  }
  return {
    heading: confirmHeading(cands.length),
    lines: [...groups].map(([label, g]) => confirmLine(label, g.count, g.records, g.would)),
  }
}

/** The kinds that suit every message: in each set, decidable, and needing no record or outcome. */
export function changeKindChoices(
  sets: readonly (readonly TriageKindOption[])[],
): { choices: TriageKindOption[]; excluded: boolean } {
  if (!sets.length) return { choices: [], excluded: false }
  const common = sets[0].filter((option) =>
    sets.every((set) => set.some((other) => other.id === option.id && other.canDecide)))
  const plain = (option: TriageKindOption) => !option.takesTarget && !option.outcomes?.length
  const needsMore = (id: string) => sets.some((set) => {
    const option = set.find((other) => other.id === id)
    return !!option && !plain(option)
  })
  return {
    choices: common.filter((option) => !needsMore(option.id)),
    excluded: common.some((option) => needsMore(option.id)),
  }
}
